package com.neurobs.ccab

import com.neurobs.errors.{FutureResult, Result}
import com.neurobs.util.Guid

object IdentityGroup extends Enumeration {
	val SiteAdmin = Value("site_admin")
	val AccountAdmin = Value("account_admin")

	def displayName(group: IdentityGroup.Value): String = group match {
		case SiteAdmin => "Site Admin"
		case AccountAdmin => "Account Admin"
		case _ => group.toString
	}
}

/** Data stored about a user by the identity service
 *
 * @param userGuid Unique GUID identifying the user
 * @param accountGuid Unique GUID identifying the account of the user
 * @param email Email used to sign in and for MFA and communications
 * @param givenName First name
 * @param familyName Last name
 * @param legacyAccountId Database id for the account record of the user's account
 * @param legacyUserId Database id for the user's matching record in the database
 * @param groups List of IdentityGroup's to which the user belongs
 */
case class IdentityData
(
	userUuid: Guid,
	accountUuid: Guid,
	email: String,
	givenName: String,
	familyName: String,
	legacyAccountId: Long,
	legacyUserId: Long,
	groups: Seq[IdentityGroup.Value]
) {
	def inGroup(group: IdentityGroup.Value): Boolean = groups.contains(group)
	def isSiteAdmin: Boolean = inGroup(IdentityGroup.SiteAdmin)
	def isRootUser: Boolean = inGroup(IdentityGroup.AccountAdmin)
	def accountId: Long = legacyAccountId
	def userId: Long = legacyUserId

	def isAccountAdmin(accountId: Long): Boolean = isSiteAdmin || (isRootUser && (accountId == this.legacyAccountId))
	def userDisplay: String = email
}

trait IdentityProvider {
	import IdentityProvider._

	/** Creates a new user
	 *
	 * @param data Data for the new user.
	 * @param password If not None, the user is created and confirmed with this password, and
	 *                 the provider will not send a message to the user
	 * @return Error or IdentityData with the new userGuid member
	 */
	def createUser(data: IdentityData, password: Option[String] = None): FutureResult[IdentityData]

	def setPassword(email: String, password: String): FutureResult[Unit]

	def setProperties(email: String, givenName: String, familyName: String): FutureResult[Unit]

	def addUserToGroup(email: String, group: IdentityGroup.Value): FutureResult[Unit]

	def removeUserFromGroup(email: String, group: IdentityGroup.Value): FutureResult[Unit]

	def groupsForUser(email: String): FutureResult[Seq[IdentityGroup.Value]]

	def setDisabled(email: String, disabled: Boolean): FutureResult[Unit]

	/** Resends any new user message to the specified user already created with createUser
	 *
	 * @param email email for the user given to createUser
	 * @return Error if any
	 */
	def resendNewUserMessage(email: String): FutureResult[Unit]

	/** Authenticates a user given email and password
	 *
	 * @param email Unique email for the user
	 * @param password The user's password
	 * @return Results of the authentication
	 */
	def authenticateUser(email: String, password: String): FutureResult[Option[AccessData]]

	def decodeIdToken(idToken: String): Result[Option[IdentityData]]
}

object IdentityProvider {
	case class AccessData
	(
		accessToken: String,
		refreshToken: String,
		idToken: String
	)
}